import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import AriaModal from 'react-aria-modal';
import { connect } from 'react-redux';
import { addAural } from 'actions/aural_notification';

let nextId = 1;

const btnType = PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func, // Not required for dismiss buttons
    primary: PropTypes.bool,
    className: PropTypes.string,
    dismiss: PropTypes.bool,
});

class Dialog extends React.Component {
    static defaultProps = {
        buttons: [],
        closeButton: true,
        onClose: function() {},
    };

    static propTypes = {
        buttons: PropTypes.arrayOf(btnType),
        children: PropTypes.node.isRequired,
        className: PropTypes.string,
        closeButton: PropTypes.bool,
        onClose: PropTypes.func,
        title: PropTypes.string.isRequired,
        // https://github.com/davidtheclark/react-aria-modal#focusdialog
        focusDialog: PropTypes.bool,
        underlayClickExits: PropTypes.bool,
        addAural: PropTypes.func.isRequired,
    };

    triggerElement = document.activeElement;

    state = {
        id: nextId++,
        hasEntered: false,
    };

    componentWillUnmount() {
        this.isUnmounting = true;
    }

    onAriaExit = () => {
        // If the AriaModal is unmounting because it was removed from the DOM we
        // don't need to call this handler
        if (!this.isUnmounting) {
            this.dismiss();
        }
    };

    onModalEnter = () => {
        this.setState({ modalHasEntered: true });
    };

    btnConverter = (b, index) => {
        const btnClasses = classnames(
            {
                btn: true,
                'js-btn': true,
                'btn-primary': b.primary,
            },
            b.className
        );

        const onClick = () => {
            if (b.primary) {
                // Don't do anything with primary buttons. The `<form>` will use
                // their `onClick`
            } else if (b.dismiss) {
                this.dismiss();
            } else {
                b.onClick();
            }
        };

        return (
            <input
                type={b.primary ? 'submit' : 'button'}
                disabled={b.disabled}
                key={'btn' + index}
                className={btnClasses}
                onClick={onClick}
                value={b.label}
            />
        );
    };

    dismiss = () => {
        this.props.addAural(`"${this.props.title}" closed`);
        const dismissButtons = this.props.buttons.filter(b => b.dismiss);

        if (dismissButtons.length === 1 && dismissButtons[0].onClick) {
            if (dismissButtons[0].onClick() === false) {
                return;
            }
        }

        this.setState({
            modalHasEntered: false,
        });
        // Wait for the animation to complete before notifying external
        // components that the modal has closed
        setTimeout(() => {
            this.props.onClose();
        }, 300);
    };

    renderCloseButton = () => {
        if (this.props.closeButton) {
            return (
                <button onClick={this.dismiss} type="button" className="close">
                    <span className="aural">Close modal.</span>
                    <span aria-hidden="true">×</span>
                </button>
            );
        }
    };

    renderModal = children => {
        const { title, className } = this.props;
        const { modalHasEntered } = this.state;

        const wrappedChildren = this.props.buttons.reduce((c, btn) => {
            if (btn.primary) {
                const onSubmit = e => {
                    e.preventDefault();
                    btn.onClick(e);
                };
                return <form onSubmit={onSubmit}>{c}</form>;
            }
            return c;
        }, children);
        const underlayClasses = classnames(
            {
                underlay: true,
                'has-entered': modalHasEntered,
            },
            className
        );

        return (
            <AriaModal
                mounted
                focusDialog={this.props.focusDialog}
                titleText={title}
                onEnter={this.onModalEnter}
                onExit={this.onAriaExit}
                underlayClickExits={this.props.underlayClickExits}
                underlayColor={false}
                underlayClass={underlayClasses}
            >
                {wrappedChildren}
            </AriaModal>
        );
    };

    render() {
        const { title, className } = this.props;
        const { id, modalHasEntered } = this.state;
        const icon = false;

        const buttons = this.props.buttons.map(this.btnConverter);

        // Buttons don't tend to change. This is just here to catch a mistake
        // easy to make in development
        if (process.env.NODE_ENV !== 'production') {
            const dismissButtons = this.props.buttons.filter(b => b.dismiss);
            if (dismissButtons.length > 1) {
                throw new Error('Too many dismiss buttons');
            }
        }

        const footer =
            buttons.length === 0 ? (
                false
            ) : (
                <div className="modal-footer">{buttons}</div>
            );

        const mainClasses = classnames(
            {
                'modal-dialog animated': true,
                'has-entered': modalHasEntered,
            },
            className
        );

        return this.renderModal(
            <div className={mainClasses} aria-labelledby={id + 'Label'}>
                <div className="modal-content" role="document">
                    <div className="modal-header">
                        <h2 id={id + 'Label'}>
                            {icon}
                            {title}
                        </h2>
                    </div>
                    <div className="modal-body">{this.props.children}</div>
                    {footer}

                    {this.renderCloseButton()}
                </div>
            </div>
        );
    }
}

export default connect(() => ({}), { addAural })(Dialog);



// WEBPACK FOOTER //
// ./src/components/dialog.jsx